import React, { useCallback } from 'react';
import { ClientSideValidationError } from '@atlassian/jira-business-error-handling';
import { fg } from '@atlassian/jira-feature-gating';
import { getClipboardHandlerComponent } from '../../common/clipboard-handler';
import type { ListIssueFieldTransformedUnionForCopyPaste, ClipboardItem } from '../../common/types';
import {
	addToClipboard,
	getCellValue,
	getFieldMetadataFromClipboard,
	notImplemented,
} from './utils';

export const useClipboard = (
	issue?: {
		key?: string;
		issueId?: string;
		issueType?: {
			level?: number;
		};
	},
	field?: ListIssueFieldTransformedUnionForCopyPaste,
) => {
	const copyCell = useCallback(() => {
		if (!field) {
			throw new ClientSideValidationError("Can't copy, field not provided");
		}

		const { html, plainText } = getCellValue(field, issue);

		if (!plainText || !html) {
			throw new ClientSideValidationError(
				'Failed to get cell value, getter returned null or not available',
			);
		}

		return addToClipboard(plainText, html);
	}, [field, issue]);

	const renderPasteHandlerComponent = ({
		clipboard,
		onPaste,
		onBeforePaste,
	}: {
		clipboard: ClipboardItem;
		onPaste: (success: boolean) => void;
		onBeforePaste?: () => void;
	}) => {
		if (!field || !issue?.key || !issue?.issueId) {
			return;
		}

		const PasteHandlerComponent = getClipboardHandlerComponent(field.type);

		if (!PasteHandlerComponent || !clipboard?.plainText || !clipboard?.html) {
			return;
		}

		const metadata = getFieldMetadataFromClipboard(clipboard);

		return (
			<PasteHandlerComponent
				issue={{ key: issue.key, issueId: issue.issueId, issueType: issue?.issueType }}
				field={field}
				action="paste"
				clipboardItem={clipboard}
				onBeforeAction={onBeforePaste}
				onComplete={onPaste}
				metadata={metadata}
			/>
		);
	};

	const renderCutHandlerComponent = ({ onCut }: { onCut: (success: boolean) => void }) => {
		if (!field || !issue?.key || !issue?.issueId) {
			return;
		}

		const CutHandlerComponent = getClipboardHandlerComponent(field.type);

		if (!CutHandlerComponent) {
			return;
		}

		return (
			<CutHandlerComponent
				issue={{ key: issue.key, issueId: issue.issueId }}
				field={field}
				action="cut"
				onComplete={onCut}
				metadata={{ fieldId: field.fieldId, fieldType: field.type, fieldValue: null }}
			/>
		);
	};

	if (!fg('jira_list_keyboard_nav')) {
		return {
			copyCell: notImplemented,
			renderPasteHandlerComponent: () => <></>,
			renderCutHandlerComponent: () => <></>,
		};
	}

	return {
		copyCell,
		renderPasteHandlerComponent,
		renderCutHandlerComponent,
	};
};
